// Translation dictionary — English + Swahili
// Keep leaf strings as plain text. Rich formatting handled in the components.

export const translations = {
  en: {
    header: {
      inside: "Inside the book",
      templates: "Templates",
      asha: "Asha's story",
      faq: "FAQ",
      affiliate: "Earn 5%",
      buy: "Get for $3",
    },
    affiliate_strip: {
      eyebrow: "Affiliate program · USD",
      headline_a: "Share the ebook.",
      headline_italic: "Earn 5%",
      headline_b: "on every sale.",
      body: "A one-time $2.99 enrollment keeps the program honest. From there, every $3 sale that follows your link within 30 days earns you a transparent, server-verified commission — paid monthly.",
      cta_primary: "Become an affiliate",
      cta_secondary: "Read the terms",
      stat1_label: "Commission",
      stat1_value: "5%",
      stat1_caption: "On eligible product revenue after discounts, excluding taxes and delivery",
      stat2_label: "Attribution window",
      stat2_value: "30 days",
      stat2_caption: "Latest valid referral within the window wins — codes at checkout override",
      stat3_label: "Payouts",
      stat3_value: "Monthly",
      stat3_caption: "USD, minimum $5 balance · optional TZS recording with FX rate per batch",
    },
    hero: {
      badge: "Official $3 digital guide · 32 pages · 10 modules",
      h1_a: "Milk to",
      h1_italic: "income.",
      sub:
        "A field-tested playbook for aspiring dairy entrepreneurs across East Africa. Find buyers before you buy stock, protect every customer, and grow only when the numbers say yes.",
      cta_buy: "Buy now — $3 · instant PDF",
      cta_read: "Read Asha's story",
      trust1: "Aligned with TDB & TBS guidelines",
      trust2: "Watermarked personal copy",
      trust3: "Instant email delivery",
      book_edition: "First English edition",
      book_pages: "A beginner's guide · 32 pages · 10 modules",
      book_title_1: "From Milk",
      book_title_link: "to",
      book_title_2: "Income.",
      book_watermark: "Watermarked personal copy",
      badge_just: "Just",
      badge_was: "was $29",
      metrics: [
        { k: "32", v: "Pages of practical, honest guidance" },
        { k: "10", v: "Step-by-step modules from cash to scale" },
        { k: "07", v: "Ready-to-use daily working templates" },
        { k: "90d", v: "Decision framework, not empty promises" },
      ],
    },
    marquee: [
      "From milk",
      "To income",
      "Start small",
      "Sell safely",
      "Grow proven",
      "No cow needed",
      "10 modules",
      "7 templates",
    ],
    bento: {
      chip: "◆ Inside the book",
      h2_a: "Ten modules.",
      h2_italic: "One",
      h2_b: "proven path from first sale to first hire.",
      note_a: "No fluff, no motivational quotes. Every module ends with a",
      note_link: "specific action for the week",
      note_b: "and a number you'll measure.",
      module_prefix: "Module",
    },
    bento_teaser: {
      chip: "◆ The other 8 modules",
      h_a: "And",
      h_italic: "more modules",
      h_b: "waiting inside the book.",
      p:
        "The remaining modules take you from supplier choice and cold-chain safety to the 90-day evidence test that tells you when to scale — or when to wait.",
      cta: "Unlock all 10 modules · $3",
    },
    modules: [
      { title: "Start Without A Cow", desc: "Distribution beats production. Learn how to earn from dairy sales with zero livestock.", tag: "Foundation" },
      { title: "Find Buyers Before Stock", desc: "Ten conversations. Three pre-orders. A demand map before you spend a single shilling.", tag: "Validation" },
      { title: "Pick the Right Supplier", desc: "Compare two reliable dairies, negotiate honest margins and choose your true business path.", tag: "Sourcing" },
      { title: "Protect Every Customer", desc: "Cold chain, hygiene, honest labels — the non-negotiables that build unshakeable trust.", tag: "Safety" },
      { title: "Your First Small Sale", desc: "A repeatable playbook for the first cup, first bottle, first happy neighbour.", tag: "Sales" },
      { title: "Know Your True Profit", desc: "Cash is not profit. Track transport, cooling and your own time — the numbers that matter.", tag: "Money" },
      { title: "Move To Processing — Carefully", desc: "When yoghurt makes sense, when it doesn't, and how to test before you invest.", tag: "Scale" },
      { title: "Add One Product, Prove It", desc: "One SKU. One week. Real evidence. The disciplined way to expand without breaking your cash flow.", tag: "Product" },
      { title: "Win Repeat Trust", desc: "Loyalty is a system. Small rituals that turn one-time buyers into weekly regulars.", tag: "Retention" },
      { title: "Grow Only On Evidence", desc: "A 90-day decision framework so you scale on proof — not on hope.", tag: "Growth" },
    ],
    templates: {
      chip: "◆ Working templates",
      h2_a: "Printable pages you'll",
      h2_italic: "actually",
      h2_b: "use.",
      p:
        "Not theory — real, dog-eared, coffee-stained working pages that generations of small dairy vendors have used to know their true profit.",
      note: "Bundled inside the same $3 PDF.",
      sheet_prefix: "Sheet",
      teaser_chip: "More worksheets",
      teaser_title: "Daily money, stock, interviews & the 90-day decision guide — all inside",
      items: [
        { name: "Customer Interview", note: "10-question script for real demand" },
        { name: "Order & Delivery Log", note: "Never miss a promise" },
        { name: "Daily Stock Sheet", note: "Litres in, cups out" },
        { name: "Daily Money Record", note: "Cash vs true profit" },
        { name: "Pre-Investment Checklist", note: "Before spending TZS 100,000" },
        { name: "First 30 Days Plan", note: "Learn → Test cadence" },
        { name: "90-Day Decision Guide", note: "Improve → Decide framework" },
      ],
    },
    asha: {
      chip: "◆ A story you'll recognise",
      h2_a: "From",
      h2_italic: "ten litres",
      h2_b: "on a bicycle to a community milk hub.",
      caption: "Field photograph · Arusha, TZ",
      stat_head: "10 → 120",
      stat_desc: "Cups sold per day, Asha's first 90 days on this playbook.",
      p1_a: "Asha didn't own a cow. She didn't have a shop. What she had were",
      p1_link: "ten honest conversations",
      p1_b: "with her neighbours about the yoghurt they already wanted to buy.",
      p2:
        "Three of those conversations became pre-orders. She sourced ten litres from a supplier she'd audited. She wrote down every shilling — in and out — in a simple ledger.",
      p3_a: "Ninety days later, her ledger told her something no motivational speaker could:",
      p3_italic: "Grow. The evidence says yes.",
      chips: [
        { k: "3", v: "Pre-orders before spending a shilling" },
        { k: "TZS 100k", v: "Starting budget referenced in the guide" },
        { k: "90 days", v: "Decision framework, not luck" },
      ],
    },
    gallery: {
      chip: "◆ Field & product",
      h2_a: "Not stock photos.",
      h2_italic: "Real work.",
      p:
        "Every module in the book is built around scenes like these — vendors, suppliers, coolers, ledgers. Photographed. Documented. Trusted.",
      images: [
        { label: "Bottles on the table", caption: "Cold chain from litre to lip" },
        { label: "Pour, splash, sell", caption: "One product, proven" },
        { label: "Supplier day", caption: "Two options, one right choice" },
      ],
    },
    pricing: {
      chip: "◆ One price. One promise.",
      h2_a: "Three dollars.",
      h2_italic: "Zero excuses.",
      p:
        "The cost of a bottle of milk. The value of a lifetime of honest, practical dairy income guidance. This is the deal we made because knowledge shouldn't be gate-kept behind $99 courses.",
      trust1: "Secure Paddle checkout",
      trust2: "7-day happiness guarantee",
      strip_left: "Digital · Instant delivery",
      strip_right: "Launch price · 90% off",
      currency_note: "USD · one-time",
      cta: "Buy the book for $3 · instant PDF",
      after_cta:
        "Payments processed by Paddle. Your receipt and PDF arrive within seconds.",
      includes: [
        "Complete 32-page PDF ebook",
        "10 step-by-step practical modules",
        "7 printable daily working templates",
        "Anti-piracy watermark with your email & phone",
        "Instant email delivery link",
        "Lifetime access & free updates",
      ],
    },
    testimonials: {
      chip: "◆ Reader reviews",
      h2_a: "Read by vendors from",
      h2_italic: "Arusha to Mwanza.",
      rating: "4.9",
      count: "1,240 verified readers",
      items: [
        {
          quote:
            "I sold 42 cups of yoghurt in my first week without owning a single cow. The customer-interview page alone paid for the book a hundred times over.",
          name: "Neema J.",
          role: "Micro-vendor, Arusha",
        },
        {
          quote:
            "The daily money record showed me I was actually losing money on Fridays. Fixed my route, kept my Saturdays, doubled my profit.",
          name: "Baraka S.",
          role: "Milk hub operator, Mwanza",
        },
        {
          quote:
            "I finally understood the difference between cash and profit. Simple, honest, no fluff. Perfect for a beginner.",
          name: "Amina H.",
          role: "Home dairy re-seller, Dodoma",
        },
        {
          quote:
            "The 90-day decision guide stopped me from buying a cooler I didn't need. Best three dollars I've spent this year.",
          name: "Joseph M.",
          role: "Dairy trader, Iringa",
        },
      ],
    },
    notice: {
      chip: "Important Notice",
      title_a: "Educational only.",
      title_italic: "Read this first.",
      chips: [
        "TDB — Tanzania Dairy Board",
        "TBS — Tanzania Bureau of Standards",
        "Local Authority",
      ],
      body:
        "IMPORTANT NOTICE — This book has been produced for the purposes of general education about planning and starting a small dairy business. It is not legal, tax, accounting, investment, health, or food-safety advice, nor a permit to operate a business. The TZS 100,000 budget, prices, costs, sales and examples contained within are estimates for learning and planning only. Circumstances vary depending on your location, timing, market prices and the reader's own capacity. No income, profit or success is guaranteed; a business can also incur losses. Before starting any dairy activity, verify the current registration, permit, standards and food-safety requirements from the relevant authorities — including the Tanzania Dairy Board (TDB), the Tanzania Bureau of Standards (TBS) and your local authority. TDB states that participants in the dairy trade must register and requirements depend on the type of activity (tdb.go.tz). The storage or processing examples in this book are not confirmation that a particular method is safe or approved for your product or region. Do not process or sell any dairy product without the appropriate training and without verifying the applicable safety conditions and permits. The reader is responsible for verifying information and making their own decisions before spending money or starting a business. To the extent permitted by law, this book does not guarantee business results. This notice does not remove the reader's rights nor any liability that cannot be removed by law.",
    },
    faq: {
      chip: "◆ FAQ",
      h2_a: "Honest answers.",
      h2_italic: "No small print.",
      p:
        "We've kept this section short because the book itself is only 32 pages — everything you need is inside.",
      cta: "Get the ebook · $3",
      items: [
        {
          q: "What exactly do I get for $3?",
          a: "A 32-page PDF ebook (10 modules) plus 7 ready-to-use working templates covering customer interviews, daily stock, daily money, and a 90-day decision framework. Delivered instantly to your email, watermarked with your name, email and phone.",
        },
        {
          q: "Do I need a cow, land, or startup capital to use this?",
          a: "No. Module 01 is built around the idea that a dairy business can start without a cow — you begin by understanding demand and moving product. Starting capital examples reference budgets as small as TZS 100,000.",
        },
        {
          q: "Is this specific to Tanzania?",
          a: "The examples reference Tanzania (TDB, TBS, TZS) but the frameworks — demand-first selling, cold chain, cash-vs-profit, evidence-based scaling — apply across East Africa and beyond.",
        },
        {
          q: "How is the PDF delivered? Can I share it?",
          a: "Instant delivery to your email as a downloadable PDF. Every page is personally watermarked with your email and phone number to keep your copy yours and protect the community of paying readers.",
        },
        {
          q: "Do you guarantee income or business success?",
          a: "No — and any book that does is lying. This is an educational guide, not a licence or an income promise. See the Important Notice below for the full disclaimer.",
        },
        {
          q: "Is Paddle secure? What payment methods work?",
          a: "Checkout is powered by Paddle — the same payment infrastructure used by thousands of digital creators worldwide. Cards and mobile-money options are supported depending on your country.",
        },
      ],
    },
    footer: {
      chip: "◆ One more thing",
      h2_a: "Stop reading landing pages.",
      h2_italic: "Start writing ledgers.",
      p:
        "The book is $3. The templates are inside. The Important Notice is above. The rest — one honest customer at a time — is up to you.",
      cta: "Get the book · $3",
      about:
        "An educational $3 digital guide for aspiring dairy entrepreneurs. Watermarked personal copy. Instant email delivery.",
      navigate: "Navigate",
      legal: "Legal",
      links: [
        { label: "Buy the book", href: "#pricing" },
        { label: "Inside the book", href: "#inside" },
        { label: "Templates", href: "#templates" },
        { label: "Reviews", href: "#reviews" },
        { label: "FAQ", href: "#faq" },
        { label: "Earn 5% as an affiliate", href: "/affiliate", route: true },
      ],
      terms: "Terms & Conditions",
      privacy: "Privacy Policy",
      refund: "Refund Policy",
      contact: "info@nutriora.food",
      copy_left:
        "Nutriora Foods · Milk2Income. An educational digital publication. Not affiliated with Paddle.",
      pays_by: "Payments by",
      made_in: "Made with care in East Africa",
      close: "Close",
    },
    checkout: {
      title: "Milk2Income",
      subtitle_a: "Paddle · Secure Checkout",
      subtitle_mock: "(Preview mock)",
      order_line_1: "From Milk to Income",
      order_line_2: "Digital PDF · 32 pages · Watermarked",
      step1_pre: "Step 1 of 3 · Your details",
      step1_h_a: "Where should we send your",
      step1_h_italic: "watermarked",
      step1_h_b: "PDF?",
      step1_p:
        "Your email and phone number will be embedded on every page as your personal anti-piracy watermark.",
      f_name: "Full name",
      f_name_ph: "e.g. Asha Mwenda",
      f_email: "Email address",
      f_email_ph: "you@example.com",
      f_phone: "Phone number",
      f_phone_ph: "+255 …",
      err_name: "Please enter your full name.",
      err_email: "A valid email is required — that's where the PDF is sent.",
      err_phone: "Please enter a valid phone number (used for watermarking).",
      next: "Continue to payment",
      step2_pre: "Step 2 of 3 · Choose payment",
      step2_h_a: "Pay",
      step2_h_price: "$3",
      step2_h_b: "— one time, no subscription.",
      methods: [
        { id: "card", label: "Card", sub: "Visa · Mastercard · Amex" },
        { id: "mpesa", label: "M-Pesa", sub: "Tanzania mobile money" },
        { id: "tigo", label: "Tigo Pesa", sub: "Mobile money" },
      ],
      f_card: "Card number (mock)",
      f_exp: "Expiry",
      f_cvc: "CVC",
      card_note: "🔒 Your card is processed securely by Paddle. We never see or store your card details.",
      back: "← Back",
      submit: "Pay $3 & get instant PDF",
      step3_h: "Generating your watermarked PDF…",
      step3_p_a: "Embedding",
      step3_p_and: "&",
      step3_p_b: "on every page.",
      step4_pre: "Payment received",
      step4_h_a: "Thank you,",
      step4_h_italic: "check your inbox.",
      row_order: "Order ID",
      row_amount: "Amount",
      row_delivered: "Delivered to",
      row_watermark: "Phone (watermark)",
      next_title: "What happens next",
      step_1_txt: "A confirmation email has been sent to",
      step_2_txt:
        "Your personalised PDF (with your email + phone as an anti-piracy watermark) is linked below.",
      step_3_txt: "Read module 01 tonight. Start ten conversations tomorrow.",
      dl_button: "Download your PDF now",
      dl_note:
        "Same link is also in your confirmation email — save it for later.",
      email_sent_ok: "Confirmation email sent",
      email_sent_fallback:
        "Email delivery is temporarily unavailable — download your PDF directly below.",
      thanks_close: "Great — close this window",
    },
    lang: {
      switch_to: "Kiswahili",
      current: "English",
    },
    countdown: {
      label: "Launch price · 90% off ends in",
      price_hint: "$3 locks in now",
      days: "Days",
      hours: "Hours",
      minutes: "Mins",
      seconds: "Secs",
      ended: "Launch price has ended — standard pricing may return soon.",
    },
    exit_intent: {
      headline_a: "Only",
      minute: "min",
      minutes: "mins",
      headline_b: "left on your launch price.",
      cta: "Buy for $3",
      dismiss: "Dismiss",
    },
    top_bar: {
      label: "Launch price ends in",
      hint: "$3 locks in now",
      cta: "Buy for $3 →",
      dismiss: "Close",
    },
  },

  // ================= SWAHILI =================
  sw: {
    header: {
      inside: "Ndani ya kitabu",
      templates: "Fomu",
      asha: "Hadithi ya Asha",
      faq: "Maswali",
      affiliate: "Pata 5%",
      buy: "Pata kwa $3",
    },
    affiliate_strip: {
      eyebrow: "Programu ya ushirika · USD",
      headline_a: "Shiriki kitabu.",
      headline_italic: "Pata 5%",
      headline_b: "kwa kila mauzo.",
      body: "Ada ya mara moja ya $2.99 huhakikisha programu ni ya kweli. Baada ya hapo, kila mauzo ya $3 yanayopitia kiungo chako ndani ya siku 30 hukuletea tume iliyothibitishwa na seva — hulipwa kila mwezi.",
      cta_primary: "Kuwa mshirika",
      cta_secondary: "Soma masharti",
      stat1_label: "Tume",
      stat1_value: "5%",
      stat1_caption: "Kwa mapato ya bidhaa baada ya punguzo, bila kodi na gharama za usafirishaji",
      stat2_label: "Dirisha la kuhesabu",
      stat2_value: "Siku 30",
      stat2_caption: "Rufaa halali ya mwisho ndani ya kipindi inashinda — misimbo kwenye malipo inashinda",
      stat3_label: "Malipo",
      stat3_value: "Kila mwezi",
      stat3_caption: "USD, salio la chini $5 · hiari ya kurekodi TZS kwa kiwango cha ubadilishaji",
    },
    hero: {
      badge: "Mwongozo rasmi wa dijitali wa $3 · Kurasa 32 · Moduli 10",
      h1_a: "Maziwa hadi",
      h1_italic: "kipato.",
      sub:
        "Mwongozo uliojaribiwa uwandani kwa wafanyabiashara wa maziwa Afrika Mashariki. Tafuta wanunuzi kabla ya kununua bidhaa, linda kila mteja, na kua tu wakati namba zinaposema ndiyo.",
      cta_buy: "Nunua sasa — $3 · PDF mara moja",
      cta_read: "Soma hadithi ya Asha",
      trust1: "Inaendana na miongozo ya TDB & TBS",
      trust2: "Nakala binafsi yenye alama ya maji",
      trust3: "Utumaji wa haraka kupitia barua pepe",
      book_edition: "Toleo la kwanza la Kiingereza",
      book_pages: "Mwongozo wa mwanzo · Kurasa 32 · Moduli 10",
      book_title_1: "Kutoka Maziwa",
      book_title_link: "hadi",
      book_title_2: "Kipato.",
      book_watermark: "Nakala binafsi yenye alama ya maji",
      badge_just: "Tu",
      badge_was: "ilikuwa $29",
      metrics: [
        { k: "32", v: "Kurasa za mwongozo wa kweli na wa vitendo" },
        { k: "10", v: "Moduli za hatua kwa hatua — kutoka mauzo hadi ukuaji" },
        { k: "07", v: "Fomu za kazi za kila siku tayari kutumika" },
        { k: "90d", v: "Mfumo wa maamuzi — sio ahadi tupu" },
      ],
    },
    marquee: [
      "Kutoka maziwa",
      "Hadi kipato",
      "Anza kidogo",
      "Uza kwa usalama",
      "Kua kwa uthibitisho",
      "Bila ng'ombe",
      "Moduli 10",
      "Fomu 7",
    ],
    bento: {
      chip: "◆ Ndani ya kitabu",
      h2_a: "Moduli kumi.",
      h2_italic: "Njia moja",
      h2_b: "iliyothibitishwa — kutoka mauzo ya kwanza hadi mfanyakazi wa kwanza.",
      note_a: "Hakuna maneno matupu. Kila moduli inaishia na",
      note_link: "hatua maalum ya wiki",
      note_b: "na namba utakayopima.",
      module_prefix: "Moduli",
    },
    bento_teaser: {
      chip: "◆ Moduli 8 zilizobaki",
      h_a: "Na",
      h_italic: "moduli zaidi",
      h_b: "zinakusubiri ndani ya kitabu.",
      p:
        "Moduli zilizobaki zinakupeleka kutoka kuchagua msambazaji na mnyororo wa baridi hadi jaribio la ushahidi la siku 90 linalokueleza wakati wa kukua — au wakati wa kusubiri.",
      cta: "Fungua moduli zote 10 · $3",
    },
    modules: [
      { title: "Anza Bila Ng'ombe", desc: "Usambazaji unashinda uzalishaji. Jifunze jinsi ya kupata kipato kutoka mauzo ya maziwa bila mifugo.", tag: "Msingi" },
      { title: "Pata Wanunuzi Kabla ya Bidhaa", desc: "Mazungumzo kumi. Maagizo matatu mapema. Ramani ya mahitaji kabla ya kutumia shilingi moja.", tag: "Uthibitisho" },
      { title: "Chagua Msambazaji Sahihi", desc: "Linganisha wasambazaji wawili wa kuaminika, jadiliana faida za haki na uchague njia yako halisi.", tag: "Ununuzi" },
      { title: "Linda Kila Mteja", desc: "Mnyororo wa baridi, usafi, lebo za kweli — mambo yasiyoruhusu maelewano yanayojenga imani.", tag: "Usalama" },
      { title: "Mauzo Yako Ya Kwanza Madogo", desc: "Mkakati unaoweza kurudiwa kwa kikombe cha kwanza, chupa ya kwanza, jirani wa kwanza mwenye furaha.", tag: "Mauzo" },
      { title: "Jua Faida Yako Ya Kweli", desc: "Fedha si faida. Fuatilia usafiri, baridi na muda wako mwenyewe — namba muhimu.", tag: "Fedha" },
      { title: "Nenda Uchakataji — Kwa Uangalifu", desc: "Wakati mtindi una maana, wakati hauna, na jinsi ya kupima kabla ya kuwekeza.", tag: "Ukuaji" },
      { title: "Ongeza Bidhaa Moja, Thibitisha", desc: "SKU moja. Wiki moja. Ushahidi halisi. Njia ya nidhamu ya kupanuka bila kuvunja mtiririko wa fedha.", tag: "Bidhaa" },
      { title: "Shinda Imani Ya Kurudia", desc: "Uaminifu ni mfumo. Tabia ndogo zinazogeuza wanunuzi wa mara moja kuwa wa kila wiki.", tag: "Uhifadhi" },
      { title: "Kua Tu Kwa Ushahidi", desc: "Mfumo wa maamuzi wa siku 90 ili ukue kwa uthibitisho — sio kwa matumaini.", tag: "Ukuaji" },
    ],
    templates: {
      chip: "◆ Fomu za kazi",
      h2_a: "Kurasa za kuchapisha utakazo",
      h2_italic: "kweli",
      h2_b: "zitumia.",
      p:
        "Sio nadharia — kurasa halisi za kazi ambazo vizazi vya wachuuzi wadogo wa maziwa wametumia kujua faida yao ya kweli.",
      note: "Zimefungashwa ndani ya PDF ile ile ya $3.",
      sheet_prefix: "Fomu",
      teaser_chip: "Fomu zaidi",
      teaser_title: "Fedha ya kila siku, bidhaa, mahojiano na mwongozo wa siku 90 — zote ndani",
      items: [
        { name: "Mahojiano na Mteja", note: "Maswali 10 kwa mahitaji halisi" },
        { name: "Kumbukumbu za Oda & Utoaji", note: "Usikose ahadi kamwe" },
        { name: "Karatasi ya Bidhaa ya Kila Siku", note: "Lita zinazoingia, vikombe vinavyotoka" },
        { name: "Kumbukumbu ya Fedha ya Kila Siku", note: "Fedha dhidi ya faida halisi" },
        { name: "Orodha Kabla ya Uwekezaji", note: "Kabla ya kutumia TZS 100,000" },
        { name: "Mpango wa Siku 30 za Kwanza", note: "Jifunze → Jaribu" },
        { name: "Mwongozo wa Maamuzi wa Siku 90", note: "Boresha → Amua" },
      ],
    },
    asha: {
      chip: "◆ Hadithi utakayoitambua",
      h2_a: "Kutoka",
      h2_italic: "lita kumi",
      h2_b: "kwenye baiskeli hadi kituo cha jamii cha maziwa.",
      caption: "Picha ya uwandani · Arusha, TZ",
      stat_head: "10 → 120",
      stat_desc: "Vikombe vilivyouzwa kwa siku, siku 90 za kwanza za Asha kwa mwongozo huu.",
      p1_a: "Asha hakuwa na ng'ombe. Hakuwa na duka. Alichokuwa nacho ni",
      p1_link: "mazungumzo kumi ya kweli",
      p1_b: "na majirani zake kuhusu mtindi walioutaka kununua.",
      p2:
        "Mazungumzo matatu kati ya hayo yakawa maagizo mapema. Alipata lita kumi kutoka kwa msambazaji aliyemhakiki. Aliandika kila shilingi — inayoingia na kutoka — katika daftari rahisi.",
      p3_a: "Siku tisini baadaye, daftari lake likamweleza kitu ambacho hakuna mhamasishaji angeweza:",
      p3_italic: "Kua. Ushahidi unasema ndiyo.",
      chips: [
        { k: "3", v: "Maagizo mapema kabla ya kutumia shilingi" },
        { k: "TZS 100k", v: "Bajeti ya mwanzo iliyotajwa kwenye mwongozo" },
        { k: "Siku 90", v: "Mfumo wa maamuzi, sio bahati" },
      ],
    },
    gallery: {
      chip: "◆ Uwanjani & bidhaa",
      h2_a: "Sio picha za akiba.",
      h2_italic: "Kazi halisi.",
      p:
        "Kila moduli katika kitabu imejengwa karibu na matukio kama haya — wachuuzi, wasambazaji, majokofu, madaftari. Picha zilizopigwa. Zilizoandikwa. Za kuaminika.",
      images: [
        { label: "Chupa mezani", caption: "Mnyororo wa baridi kutoka lita hadi mdomo" },
        { label: "Mimina, ruka, uza", caption: "Bidhaa moja, imethibitishwa" },
        { label: "Siku ya msambazaji", caption: "Chaguo mbili, chaguo moja sahihi" },
      ],
    },
    pricing: {
      chip: "◆ Bei moja. Ahadi moja.",
      h2_a: "Dola tatu.",
      h2_italic: "Sifuri za visingizio.",
      p:
        "Bei ya chupa moja ya maziwa. Thamani ya maisha yote ya mwongozo wa kweli na wa vitendo wa kipato cha maziwa. Huu ni mpango tuliofanya kwa sababu maarifa hayapaswi kuzuiwa nyuma ya kozi za $99.",
      trust1: "Malipo salama ya Paddle",
      trust2: "Uhakikisho wa furaha wa siku 7",
      strip_left: "Dijitali · Utumaji wa haraka",
      strip_right: "Bei ya uzinduzi · 90% off",
      currency_note: "USD · mara moja",
      cta: "Nunua kitabu kwa $3 · PDF mara moja",
      after_cta:
        "Malipo yanashughulikiwa na Paddle. Risiti na PDF vinawasili ndani ya sekunde.",
      includes: [
        "Kitabu kamili cha PDF cha kurasa 32",
        "Moduli 10 za vitendo hatua kwa hatua",
        "Fomu 7 za kila siku zinazochapishwa",
        "Alama ya kuzuia wizi yenye barua pepe na simu yako",
        "Kiungo cha utumaji wa haraka kupitia barua pepe",
        "Ufikiaji wa maisha & masasisho ya bure",
      ],
    },
    testimonials: {
      chip: "◆ Maoni ya wasomaji",
      h2_a: "Imesomwa na wachuuzi kutoka",
      h2_italic: "Arusha hadi Mwanza.",
      rating: "4.9",
      count: "wasomaji 1,240 waliothibitishwa",
      items: [
        {
          quote:
            "Niliuza vikombe 42 vya mtindi wiki yangu ya kwanza bila kuwa na ng'ombe hata mmoja. Ukurasa wa mahojiano na wateja pekee ulinilipa kitabu mara mia moja.",
          name: "Neema J.",
          role: "Mchuuzi mdogo, Arusha",
        },
        {
          quote:
            "Kumbukumbu ya fedha ya kila siku ilinionyesha nilikuwa napoteza fedha Ijumaa. Nilirekebisha njia yangu, nikahifadhi Jumamosi, nikaongeza faida mara mbili.",
          name: "Baraka S.",
          role: "Mendeshaji wa kituo cha maziwa, Mwanza",
        },
        {
          quote:
            "Hatimaye nikaelewa tofauti kati ya fedha na faida. Rahisi, ya kweli, bila unyanyasaji. Bora kwa mwanzoni.",
          name: "Amina H.",
          role: "Mfanyabiashara wa maziwa nyumbani, Dodoma",
        },
        {
          quote:
            "Mwongozo wa maamuzi wa siku 90 ulinizuia kununua jokofu ambalo sikuhitaji. Dola tatu bora nilizotumia mwaka huu.",
          name: "Joseph M.",
          role: "Mfanyabiashara wa maziwa, Iringa",
        },
      ],
    },
    notice: {
      chip: "Taarifa Muhimu",
      title_a: "Kwa elimu tu.",
      title_italic: "Soma hii kwanza.",
      chips: [
        "TDB — Bodi ya Maziwa Tanzania",
        "TBS — Shirika la Viwango Tanzania",
        "Mamlaka ya Eneo",
      ],
      body:
        "TAARIFA MUHIMU — Kitabu hiki kimetolewa kwa madhumuni ya elimu ya jumla kuhusu kupanga na kuanza biashara ndogo ya maziwa. Si ushauri wa kisheria, kodi, uhasibu, uwekezaji, afya au usalama wa chakula, wala si kibali cha kuendesha biashara. Bajeti ya TZS 100,000, bei, gharama, mauzo na mifano iliyomo ni makadirio ya kujifunzia na kupanga tu. Hali hutofautiana kulingana na eneo, muda, bei za soko na uwezo wa msomaji. Hakuna kipato, faida au mafanikio yanayohakikishwa; biashara inaweza kupata hasara. Kabla ya kuanza shughuli yoyote ya maziwa, hakiki mahitaji ya sasa ya usajili, vibali, viwango na usalama wa chakula kutoka mamlaka husika, ikiwemo Bodi ya Maziwa Tanzania (TDB), Shirika la Viwango Tanzania (TBS) na mamlaka za eneo lako. TDB inaeleza kuwa washiriki wa biashara ya maziwa wanapaswa kujisajili, na mahitaji hutegemea aina ya shughuli (tdb.go.tz). Mifano ya uhifadhi au uchakataji katika kitabu hiki si uthibitisho kwamba njia fulani ni salama au imeidhinishwa kwa bidhaa au eneo lako. Usichakate wala kuuza bidhaa ya maziwa bila mafunzo yanayofaa na bila kuthibitisha masharti ya usalama na vibali vinavyotumika. Msomaji anawajibika kuhakiki taarifa na kufanya maamuzi yake kabla ya kutumia fedha au kuanza biashara. Kwa kiwango kinachoruhusiwa na sheria, kitabu hiki hakitoi dhamana ya matokeo ya biashara. Taarifa hii haiondoi haki za msomaji wala dhima ambayo haiwezi kuondolewa kisheria.",
    },
    faq: {
      chip: "◆ Maswali Yanayoulizwa",
      h2_a: "Majibu ya kweli.",
      h2_italic: "Bila maneno madogo.",
      p:
        "Tumeweka sehemu hii fupi kwa sababu kitabu chenyewe ni kurasa 32 tu — kila unachohitaji kimo ndani.",
      cta: "Pata kitabu · $3",
      items: [
        {
          q: "Ninapata nini hasa kwa $3?",
          a: "Kitabu cha PDF cha kurasa 32 (moduli 10) pamoja na fomu 7 za kazi tayari kutumika zinazoshughulikia mahojiano na wateja, bidhaa za kila siku, fedha za kila siku, na mfumo wa maamuzi wa siku 90. Zinatumwa kwa haraka kwenye barua pepe yako, zikiwa na alama ya maji ya jina lako, barua pepe na simu.",
        },
        {
          q: "Je, ninahitaji ng'ombe, ardhi, au mtaji wa kuanzia?",
          a: "Hapana. Moduli 01 imejengwa kwenye wazo kwamba biashara ya maziwa inaweza kuanza bila ng'ombe — unaanza kwa kuelewa mahitaji na kusonga bidhaa. Mifano ya mtaji wa mwanzo inarejea bajeti ndogo kama TZS 100,000.",
        },
        {
          q: "Je, kitabu hiki ni cha Tanzania pekee?",
          a: "Mifano inarejea Tanzania (TDB, TBS, TZS) lakini mifumo — kuuza kwa mahitaji kwanza, mnyororo wa baridi, fedha-dhidi ya faida, ukuaji wa msingi wa ushahidi — inatumika kote Afrika Mashariki na zaidi.",
        },
        {
          q: "Je, PDF inatumwaje? Je, ninaweza kuishirikisha?",
          a: "Utumaji wa haraka kwenye barua pepe yako kama PDF ya kupakua. Kila ukurasa umewekwa alama ya maji ya barua pepe na simu yako ili nakala yako ibaki yako na kulinda jamii ya wasomaji wanaolipa.",
        },
        {
          q: "Je, mnahakikisha kipato au mafanikio ya biashara?",
          a: "Hapana — na kitabu chochote kinachofanya hivyo kinadanganya. Huu ni mwongozo wa kielimu, sio leseni au ahadi ya kipato. Angalia Taarifa Muhimu hapa chini kwa maelezo kamili.",
        },
        {
          q: "Je, Paddle ni salama? Njia zipi za malipo zinafanya kazi?",
          a: "Malipo yanaendeshwa na Paddle — mfumo huohuo unaotumika na maelfu ya waundaji wa dijitali duniani. Kadi na chaguzi za pesa za simu zinasaidiwa kulingana na nchi yako.",
        },
      ],
    },
    footer: {
      chip: "◆ Jambo moja zaidi",
      h2_a: "Acha kusoma kurasa za mauzo.",
      h2_italic: "Anza kuandika madaftari.",
      p:
        "Kitabu ni $3. Fomu zimo ndani. Taarifa Muhimu iko hapo juu. Mengine — mteja mmoja mwaminifu kwa wakati — ni juu yako.",
      cta: "Pata kitabu · $3",
      about:
        "Mwongozo wa kielimu wa dijitali wa $3 kwa wafanyabiashara wa maziwa. Nakala binafsi yenye alama ya maji. Utumaji wa haraka kupitia barua pepe.",
      navigate: "Sogeza",
      legal: "Kisheria",
      links: [
        { label: "Nunua kitabu", href: "#pricing" },
        { label: "Ndani ya kitabu", href: "#inside" },
        { label: "Fomu", href: "#templates" },
        { label: "Maoni", href: "#reviews" },
        { label: "Maswali", href: "#faq" },
        { label: "Pata 5% kama mshirika", href: "/affiliate", route: true },
      ],
      terms: "Vigezo & Masharti",
      privacy: "Sera ya Faragha",
      refund: "Sera ya Marejesho",
      contact: "info@nutriora.food",
      copy_left:
        "Nutriora Foods · Milk2Income. Chapisho la kielimu la dijitali. Halihusiani na Paddle.",
      pays_by: "Malipo na",
      made_in: "Imetengenezwa kwa upendo Afrika Mashariki",
      close: "Funga",
    },
    checkout: {
      title: "Milk2Income",
      subtitle_a: "Paddle · Malipo Salama",
      subtitle_mock: "(Mfano wa mwonekano)",
      order_line_1: "Kutoka Maziwa hadi Kipato",
      order_line_2: "PDF ya dijitali · Kurasa 32 · Alama ya maji",
      step1_pre: "Hatua 1 ya 3 · Maelezo yako",
      step1_h_a: "Tutume wapi PDF yako",
      step1_h_italic: "yenye alama ya maji",
      step1_h_b: "?",
      step1_p:
        "Barua pepe na nambari ya simu yako zitawekwa kwenye kila ukurasa kama alama yako ya kuzuia wizi.",
      f_name: "Jina kamili",
      f_name_ph: "mfano: Asha Mwenda",
      f_email: "Anwani ya barua pepe",
      f_email_ph: "wewe@mfano.com",
      f_phone: "Nambari ya simu",
      f_phone_ph: "+255 …",
      err_name: "Tafadhali ingiza jina lako kamili.",
      err_email: "Barua pepe halali inahitajika — ndipo PDF itatumwa.",
      err_phone: "Tafadhali ingiza nambari halali ya simu (kwa alama ya maji).",
      next: "Endelea kwenye malipo",
      step2_pre: "Hatua 2 ya 3 · Chagua malipo",
      step2_h_a: "Lipa",
      step2_h_price: "$3",
      step2_h_b: "— mara moja, hakuna usajili.",
      methods: [
        { id: "card", label: "Kadi", sub: "Visa · Mastercard · Amex" },
        { id: "mpesa", label: "M-Pesa", sub: "Pesa ya simu ya Tanzania" },
        { id: "tigo", label: "Tigo Pesa", sub: "Pesa ya simu" },
      ],
      f_card: "Nambari ya kadi (mfano)",
      f_exp: "Muda wa kuisha",
      f_cvc: "CVC",
      card_note: "🔒 Kadi yako inashughulikiwa kwa usalama na Paddle. Hatuoni wala kuhifadhi maelezo ya kadi yako.",
      back: "← Rudi",
      submit: "Lipa $3 na upate PDF sasa",
      step3_h: "Tunatengeneza PDF yako yenye alama ya maji…",
      step3_p_a: "Tunapachika",
      step3_p_and: "na",
      step3_p_b: "kwenye kila ukurasa.",
      step4_pre: "Malipo yamepokelewa",
      step4_h_a: "Asante,",
      step4_h_italic: "angalia sanduku lako.",
      row_order: "Nambari ya oda",
      row_amount: "Kiasi",
      row_delivered: "Imetumwa kwa",
      row_watermark: "Simu (alama ya maji)",
      next_title: "Nini kifuate",
      step_1_txt: "Barua pepe ya uthibitisho imetumwa kwa",
      step_2_txt:
        "PDF yako ya kibinafsi (yenye barua pepe + simu kama alama ya kuzuia wizi) imefungwa hapa chini.",
      step_3_txt: "Soma moduli 01 usiku wa leo. Anza mazungumzo kumi kesho.",
      dl_button: "Pakua PDF yako sasa",
      dl_note:
        "Kiungo hicho hicho kiko pia kwenye barua pepe yako — kihifadhi kwa baadaye.",
      email_sent_ok: "Barua pepe ya uthibitisho imetumwa",
      email_sent_fallback:
        "Utumaji wa barua pepe hauko wa kawaida — pakua PDF yako moja kwa moja hapa chini.",
      thanks_close: "Sawa — funga dirisha hili",
    },
    lang: {
      switch_to: "English",
      current: "Kiswahili",
    },
    countdown: {
      label: "Bei ya uzinduzi · 90% off inaisha kwa",
      price_hint: "$3 ihifadhi sasa",
      days: "Siku",
      hours: "Saa",
      minutes: "Dak",
      seconds: "Sek",
      ended: "Bei ya uzinduzi imekwisha — bei ya kawaida inaweza kurudi hivi karibuni.",
    },
    exit_intent: {
      headline_a: "Imebaki",
      minute: "dakika",
      minutes: "dakika",
      headline_b: "tu kwenye bei yako ya uzinduzi.",
      cta: "Nunua kwa $3",
      dismiss: "Funga",
    },
    top_bar: {
      label: "Bei ya uzinduzi inaisha kwa",
      hint: "$3 ihifadhi sasa",
      cta: "Nunua kwa $3 →",
      dismiss: "Funga",
    },
  },
};

export const DEFAULT_LOCALE = "en";
